import React, { useState, useEffect, useCallback } from 'react';
import { workflowsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AppDrawer } from './ui/AppDrawer';
import { Loader2, Workflow, Calendar, CheckSquare, ChevronRight, Zap } from 'lucide-react';
import { toast } from 'sonner';

export function ApplyWorkflowButton({ jobId, jobMode, onApplied, size = 'sm', externalOpen, onExternalClose, hideButton }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWf, setSelectedWf] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const isOpen = externalOpen != null ? externalOpen : drawerOpen;
  const setIsOpen = (v) => {
    if (externalOpen != null) { if (!v && onExternalClose) onExternalClose(); }
    else setDrawerOpen(v);
  };

  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await workflowsAPI.list({ job_mode: jobMode });
      setWorkflows((res.data.workflows || []).filter(w => w.is_active));
    } catch { toast.error('Failed to load workflows'); }
    finally { setLoading(false); }
  }, [jobMode]);

  const handleOpen = () => {
    setSelectedWf(null);
    setPreview(null);
    setIsOpen(true);
    fetchWorkflows();
  };

  // Handle external open trigger
  React.useEffect(() => {
    if (externalOpen && !drawerOpen) {
      setSelectedWf(null);
      setPreview(null);
      fetchWorkflows();
    }
  }, [externalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = async (wf) => {
    setSelectedWf(wf);
    setPreviewLoading(true);
    try {
      const res = await workflowsAPI.preview(jobId, { workflow_id: wf.id });
      setPreview(res.data);
    } catch { toast.error('Failed to preview workflow'); }
    finally { setPreviewLoading(false); }
  };

  const handleApply = async () => {
    if (!selectedWf) return;
    setApplying(true);
    try {
      const res = await workflowsAPI.apply(jobId, { workflow_id: selectedWf.id });
      const d = res.data;
      let msg = `"${d.workflow_name}" applied — ${d.phases_created} phases, ${d.tasks_created} tasks`;
      if (d.tasks_auto_assigned > 0) msg += `, ${d.tasks_auto_assigned} auto-assigned`;
      if (d.tasks_unassigned?.length > 0) msg += `. ${d.tasks_unassigned.length} task(s) need manual assignment.`;
      toast.success(msg);
      setIsOpen(false);
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply workflow');
    } finally { setApplying(false); }
  };

  return (
    <>
      {!hideButton && (
        <Button variant="outline" size={size} onClick={handleOpen} data-testid="apply-workflow-btn">
          <Zap className="w-4 h-4 mr-1.5" /> Apply Workflow
        </Button>
      )}

      <AppDrawer open={isOpen} onOpenChange={setIsOpen} title="Apply Workflow" description="Choose a workflow to auto-generate schedule phases and tasks for this job">
        <div className="space-y-4 p-1">
          {!selectedWf ? (
            /* Workflow picker */
            loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : workflows.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground" data-testid="no-workflows">
                <Workflow className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No workflows available</p>
                <p className="text-sm mt-1">Create workflows in Settings first.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="workflow-picker">
                {workflows.map(wf => (
                  <button
                    key={wf.id}
                    onClick={() => handleSelect(wf)}
                    className="w-full text-left border rounded-lg p-3 hover:border-primary/50 hover:bg-muted/30 transition-all flex items-center gap-3"
                    data-testid={`pick-workflow-${wf.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{wf.name}</p>
                      {wf.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{wf.description}</p>}
                      <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{wf.schedule_phase_count} phases</span>
                        <span className="flex items-center gap-1"><CheckSquare className="w-3 h-3" />{wf.task_count} tasks</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>
                ))}
              </div>
            )
          ) : (
            /* Preview + confirm */
            <div className="space-y-4">
              <button onClick={() => { setSelectedWf(null); setPreview(null); }} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors" data-testid="wf-back-to-list">
                <ChevronRight className="w-3 h-3 rotate-180" /> Back to list
              </button>

              <div className="border rounded-lg p-3 bg-muted/10">
                <p className="text-sm font-semibold">{selectedWf.name}</p>
                {selectedWf.description && <p className="text-xs text-muted-foreground mt-0.5">{selectedWf.description}</p>}
              </div>

              {previewLoading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : preview ? (
                <div className="space-y-4">
                  {/* Phases preview */}
                  {preview.phases.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Schedule Phases ({preview.phases.length})</h4>
                      <div className="border rounded-lg divide-y">
                        {preview.phases.map((p, i) => (
                          <div key={i} className="flex items-center gap-2 p-2.5 text-sm">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                            <span className="flex-1 font-medium">{p.name}</span>
                            <span className="text-xs text-muted-foreground">{p.start_date} — {p.end_date}</span>
                            {p.duration_days && <Badge variant="secondary" className="text-[10px]">{p.duration_days}d</Badge>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tasks preview */}
                  {preview.tasks.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Tasks ({preview.tasks.length})</h4>
                      <div className="border rounded-lg divide-y">
                        {preview.tasks.map((t, i) => (
                          <div key={i} className="flex items-center gap-2 p-2.5 text-sm">
                            <span className="flex-1 min-w-0">
                              <span className="font-medium">{t.title}</span>
                              {t.linked_phase && <span className="text-[10px] text-muted-foreground ml-1.5">({t.linked_phase})</span>}
                            </span>
                            {t.assigned_role && <Badge variant="secondary" className="text-[10px] capitalize">{t.assigned_role}</Badge>}
                            {t.category && <Badge variant="outline" className="text-[10px]">{t.category}</Badge>}
                            {t.due_date && <span className="text-xs text-muted-foreground flex-shrink-0">{t.due_date}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">Based on job start date: {preview.job_start}</p>

                  <Button onClick={handleApply} disabled={applying} className="w-full" data-testid="confirm-apply-workflow">
                    {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                    Apply "{selectedWf.name}"
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </AppDrawer>
    </>
  );
}
